// src/pages/admin/UserDetail.jsx
// Detailed view of a single user with all relevant information

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, User, Mail, Shield, Calendar, Clock, CreditCard,
  Activity, Users, ListChecks, Lock, Unlock, Key, UserCog,
  AlertTriangle, Check, X, Phone, Building, FileText
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ROLES = [
  { value: "agent", label: "Agent", color: "bg-blue-100 text-blue-800", description: "Standard user - can manage their own clients" },
  { value: "agency_admin", label: "Agency Admin", color: "bg-teal-100 text-teal-800", description: "Agency administrator - manages agency operations" },
  { value: "agency_owner", label: "Agency Owner", color: "bg-emerald-100 text-emerald-800", description: "Agency owner - full control of agency" },
  { value: "fmo", label: "FMO", color: "bg-amber-100 text-amber-800", description: "Field Marketing Organization - manages multiple agencies" },
  { value: "founder", label: "Founder", color: "bg-purple-100 text-purple-800", description: "Platform owner - all permissions" },
];

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Role change
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [saving, setSaving] = useState(false);

  // Check current user permissions
  useEffect(() => {
    async function fetchCurrentUser() {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
        const data = await res.json();
        if (data.ok && data.user) {
          setCurrentUser(data.user);
          if (!["admin", "agency_admin", "agency_owner", "fmo", "founder"].includes(data.user.role)) {
            navigate("/dashboard");
          }
        } else {
          navigate("/login");
        }
      } catch (err) {
        navigate("/login");
      }
    }
    fetchCurrentUser();
  }, [navigate]);

  // Fetch user details
  useEffect(() => {
    async function fetchUser() {
      if (!currentUser) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/admin/users/${id}`, { credentials: "include" });
        const data = await res.json();
        if (data.ok) {
          setUser(data.user);
        } else {
          setError(data.error || "Failed to load user");
        }
      } catch (err) {
        setError("Failed to connect to server");
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [id, currentUser]);

  // Update role
  async function handleRoleChange() {
    if (!user || !newRole) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json();
      if (data.ok) {
        setUser({ ...user, role: newRole });
        setShowRoleModal(false);
        setSuccess("Role updated successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || "Failed to update role");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setSaving(false);
    }
  }

  // Unlock account
  async function handleUnlock() {
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}/unlock`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.ok) {
        setUser({ ...user, lockout_until: null, failed_login_attempts: 0 });
        setSuccess("Account unlocked successfully");
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError("Failed to unlock account");
    }
  }

  // Send password reset
  async function handleSendPasswordReset() {
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: user.email }),
      });
      if (res.ok) {
        setSuccess("Password reset email sent");
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError("Failed to send password reset");
    }
  }

  const isLocked = user?.lockout_until && new Date(user.lockout_until) > new Date();
  const roleInfo = ROLES.find(r => r.value === (user?.role || "agent"));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <AlertTriangle className="inline w-5 h-5 mr-2" />
          {error}
        </div>
        <Link to="/admin/users" className="mt-4 inline-flex items-center gap-2 text-amber-600 hover:text-amber-700">
          <ArrowLeft size={18} /> Back to User Management
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Back link */}
      <Link to="/admin/users" className="inline-flex items-center gap-2 text-gray-600 hover:text-amber-600 mb-6">
        <ArrowLeft size={18} /> Back to User Management
      </Link>

      {/* Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertTriangle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
            {(user?.email?.[0] || "?").toUpperCase()}
          </div>

          {/* User Info */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{user?.email}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 text-sm font-bold rounded-full ${roleInfo?.color}`}>
                {roleInfo?.label || "Agent"}
              </span>
              {user?.mfa_enabled && (
                <span className="px-3 py-1 text-sm font-bold rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                  <Shield size={14} /> MFA Enabled
                </span>
              )}
              {isLocked && (
                <span className="px-3 py-1 text-sm font-bold rounded-full bg-red-100 text-red-800 flex items-center gap-1">
                  <Lock size={14} /> Account Locked
                </span>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { setNewRole(user?.role || "agent"); setShowRoleModal(true); }}
              disabled={user?.id === currentUser?.id}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserCog size={18} /> Change Role
            </button>
            {isLocked && (
              <button
                onClick={handleUnlock}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg"
              >
                <Unlock size={18} /> Unlock Account
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Account Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-amber-500" /> Account Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm text-gray-500">Email</div>
                <div className="font-medium">{user?.email}</div>
              </div>
            </div>
            {user?.phone && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div className="font-medium">{user.phone}</div>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm text-gray-500">Account Created</div>
                <div className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  }) : "Unknown"}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm text-gray-500">Last Login</div>
                <div className="font-medium">
                  {user?.last_login_at ? new Date(user.last_login_at).toLocaleString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  }) : "Never"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" /> Security
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className={`w-5 h-5 ${user?.mfa_enabled ? "text-green-500" : "text-gray-400"}`} />
                <span>Two-Factor Authentication</span>
              </div>
              <span className={`px-2 py-1 text-xs font-bold rounded ${user?.mfa_enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                {user?.mfa_enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Lock className={`w-5 h-5 ${isLocked ? "text-red-500" : "text-gray-400"}`} />
                <span>Account Status</span>
              </div>
              <span className={`px-2 py-1 text-xs font-bold rounded ${isLocked ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                {isLocked ? "Locked" : "Active"}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-gray-400" />
                <span>Failed Login Attempts</span>
              </div>
              <span className={`px-2 py-1 text-xs font-bold rounded ${(user?.failed_login_attempts || 0) > 0 ? "bg-amber-100 text-amber-800" : "bg-gray-200 text-gray-600"}`}>
                {user?.failed_login_attempts || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Activity Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-500" /> Activity
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <div className="text-2xl font-bold text-blue-800">{user?.stats?.clients || 0}</div>
              <div className="text-sm text-blue-600">Clients</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <ListChecks className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold text-green-800">{user?.stats?.tasks || 0}</div>
              <div className="text-sm text-green-600">Tasks</div>
            </div>
          </div>
          {user?.stats?.loginsLast30Days !== undefined && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-lg font-bold text-gray-800">{user.stats.loginsLast30Days}</div>
              <div className="text-sm text-gray-500">Logins in last 30 days</div>
            </div>
          )}
        </div>

        {/* Billing */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-500" /> Billing
          </h2>
          {user?.subscription ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span>Plan</span>
                <span className="font-bold text-amber-600">{user.subscription.plan}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span>Status</span>
                <span className={`px-2 py-1 text-xs font-bold rounded ${user.subscription.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                  {user.subscription.status}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No active subscription</p>
              <p className="text-sm">User is on free tier</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleSendPasswordReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg"
          >
            <Key size={18} /> Send Password Reset
          </button>
          <button
            onClick={() => window.open(`mailto:${user?.email}`, "_blank")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg"
          >
            <Mail size={18} /> Email User
          </button>
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Change Role</h3>
            <p className="text-gray-600 mb-4">
              Change role for <strong>{user?.email}</strong>
            </p>

            <div className="space-y-2 mb-6">
              {ROLES.map((role) => (
                <label
                  key={role.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    newRole === role.value ? "border-amber-500 bg-amber-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={newRole === role.value}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="accent-amber-500"
                  />
                  <div>
                    <div className="font-medium">{role.label}</div>
                    <div className="text-sm text-gray-500">{role.description}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowRoleModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleRoleChange}
                disabled={saving || newRole === user?.role}
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
